import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { FiEye, FiEyeOff, FiLayers, FiMaximize2, FiMinimize2, FiZoomIn } from 'react-icons/fi';
import TimeSeriesPlot, { resolveChartColor } from './TimeSeriesPlot';
import ChartNavigator from './ChartNavigator';
import type { ChartSpec } from './types';
import { validateChartSpec } from './validateChartSpec';
import { chartTimeMilliseconds } from './timeRange';
import { NAVIGATOR_MIN_POINTS } from './displayDensity';
import { chartLayers } from './layers';
import { buildDisplaySpec } from './scaleModel';
import { useThemeMode } from '../../design-system';
import './UnifiedChart.css';

// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const MaximizeIcon = FiMaximize2 as React.ComponentType<React.SVGProps<SVGSVGElement>>;
// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const MinimizeIcon = FiMinimize2 as React.ComponentType<React.SVGProps<SVGSVGElement>>;
// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const ZoomIcon = FiZoomIn as React.ComponentType<React.SVGProps<SVGSVGElement>>;
// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const LayersIcon = FiLayers as React.ComponentType<React.SVGProps<SVGSVGElement>>;
// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const EyeIcon = FiEye as React.ComponentType<React.SVGProps<SVGSVGElement>>;
// SAFETY: react-icons exports render SVG elements and accepts the standard SVG prop contract.
const EyeOffIcon = FiEyeOff as React.ComponentType<React.SVGProps<SVGSVGElement>>;

const DEFAULT_RANGE_PRESETS = ['1D', '1W', '1M', '3M', '6M', '1Y', '2Y', '5Y', 'ALL'] as const;
const RANGE_PRESET_NAMES = new Map<string, string>([
  ['1D', 'Show 1 day'],
  ['1W', 'Show 1 week'],
  ['1M', 'Show 1 month'],
  ['3M', 'Show 3 months'],
  ['6M', 'Show 6 months'],
  ['1Y', 'Show 1 year'],
  ['2Y', 'Show 2 years'],
  ['5Y', 'Show 5 years'],
  ['ALL', 'Show all data'],
]);

type ChartLabelMode = 'auto' | 'hover' | 'always' | 'hidden';

function chartLabelMode(value: string): ChartLabelMode {
  switch (value) {
    case 'hover':
    case 'always':
    case 'hidden':
      return value;
    default:
      return 'auto';
  }
}

type ChartScaleMode = 'linear' | 'log' | 'percentage' | 'indexed';

function chartScaleMode(value: string): ChartScaleMode {
  switch (value) {
    case 'log':
    case 'percentage':
    case 'indexed':
      return value;
    default:
      return 'linear';
  }
}

function rangePresetAccessibleName(preset: string): string {
  return RANGE_PRESET_NAMES.get(preset) || `Show ${preset}`;
}

type UnifiedChartProps = {
  spec: ChartSpec;
  sourceLabel?: string;
  observedAt?: string;
  height?: number;
  symbolSelector?: React.ReactNode;
};

export default function UnifiedChart({
  spec,
  sourceLabel,
  observedAt,
  height = 520,
  symbolSelector,
}: UnifiedChartProps): React.ReactElement {
  const chartSyncId = useId();
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  const layerSurfaceRef = useRef<HTMLDivElement>(null);
  const layersButtonRef = useRef<HTMLButtonElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [theme] = useThemeMode();
  const [expanded, setExpanded] = useState(false);
  const [layersOpen, setLayersOpen] = useState(false);
  const [layersAnchor, setLayersAnchor] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const [hiddenLayers, setHiddenLayers] = useState<Set<string>>(new Set());
  const [hiddenOverlayLayers, setHiddenOverlayLayers] = useState<Set<string>>(new Set());
  const [layerSearch, setLayerSearch] = useState('');
  const [labelMode, setLabelMode] = useState<ChartLabelMode>('auto');
  const [visibleRange, setVisibleRange] = useState<[number, number]>([0, 0]);
  const [activeRangePreset, setActiveRangePreset] = useState<string | null>('ALL');
  const [scaleMode, setScaleMode] = useState<ChartScaleMode>('linear');
  const errors = useMemo(() => (spec ? validateChartSpec(spec) : []), [spec]);
  const layers = useMemo(() => chartLayers(spec), [spec]);
  const displayModel = useMemo(
    () => buildDisplaySpec(spec, scaleMode, visibleRange),
    [scaleMode, spec, visibleRange],
  );
  const renderedSpec = displayModel.spec;

  useEffect(() => {
    const lastIndex = Math.max(0, (spec?.data.length || 1) - 1);
    setActiveIndex(lastIndex);
    setVisibleRange([0, lastIndex]);
    setActiveRangePreset('ALL');
    setHiddenOverlayLayers(new Set());
    setHiddenLayers(
      new Set(layers.filter((layer) => layer.defaultVisible === false).map((layer) => layer.id)),
    );
    setLayerSearch('');
    setLabelMode('auto');
    setLayersOpen(false);
  }, [spec, layers]);

  useEffect(() => {
    if (!expanded) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      if (layersOpen) {
        setLayersOpen(false);
        return;
      }
      setExpanded(false);
      window.requestAnimationFrame(() => expandButtonRef.current?.focus());
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [expanded, layersOpen]);

  // The panel opens against the right edge of the button that opened it. The
  // rail scrolls sideways and its width changes with the chart, so the anchor
  // is measured rather than guessed.
  useLayoutEffect(() => {
    if (!layersOpen) {
      setLayersAnchor(null);
      return undefined;
    }
    const measure = () => {
      const surface = layerSurfaceRef.current;
      const button = layersButtonRef.current;
      if (!surface || !button) return;
      setLayersAnchor(button.getBoundingClientRect().right - surface.getBoundingClientRect().left);
    };
    measure();
    const rail = railRef.current;
    window.addEventListener('resize', measure);
    // Entering the expanded view rearranges the rail without resizing the
    // window, so the anchor follows the rail's own geometry.
    const observer = window.ResizeObserver ? new window.ResizeObserver(measure) : null;
    if (observer && rail) observer.observe(rail);
    if (observer && layerSurfaceRef.current) observer.observe(layerSurfaceRef.current);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [expanded, layersOpen]);

  if (!spec) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload has no explicit series data.</p>
      </section>
    );
  }

  if (errors.length > 0) {
    return (
      <section
        className="unified-chart unified-chart--invalid"
        data-testid="unified-chart"
        role="alert"
      >
        <strong>Unable to display this chart</strong>
        <p>The chart payload is missing required structural metadata.</p>
        <ul>
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </section>
    );
  }

  const renderedPanes = spec.panes.filter((pane) =>
    spec.series.some((series) => series.paneId === pane.id),
  );
  const totalWeight = renderedPanes.reduce((sum, pane) => sum + pane.weight, 0);
  const plotHeight = expanded ? Math.max(window.innerHeight - 330, 520) : height;
  const controls = spec.controls || {};
  const showVisibilityControls = controls.allowSeriesToggle !== false && layers.length > 0;
  const filteredLayers = layers.filter((layer) =>
    `${layer.label} ${layer.description || ''}`
      .toLowerCase()
      .includes(layerSearch.trim().toLowerCase()),
  );
  const effectiveLabelMode = labelMode === 'auto' ? 'hover' : labelMode;
  const nonSeriesLayerIds = new Set([
    ...(spec.referenceLines || []).map((item) => item.layerId || item.groupId || `line_${item.id}`),
    ...(spec.referenceBands || []).map((item) => item.layerId || item.groupId || `band_${item.id}`),
    ...(spec.eventWindows || []).map((item) => item.layerId || item.groupId || `event_${item.id}`),
    ...(spec.areas || []).map((item) => item.layerId || item.groupId || `area_${item.id}`),
    ...(spec.markers || []).map((item) => item.layerId || item.groupId || `marker_${item.id}`),
  ]);
  const summaryLayers = layers.filter((layer) => nonSeriesLayerIds.has(layer.id));
  const layerMembers = (layerId: string) =>
    spec.series.filter(
      (series) => (series.layerId || series.groupId || `series_${series.id}`) === layerId,
    );
  const resetLayers = () => {
    setHiddenSeries(new Set());
    setHiddenOverlayLayers(new Set());
    setHiddenLayers(
      new Set(layers.filter((layer) => layer.defaultVisible === false).map((layer) => layer.id)),
    );
  };

  const chooseRange = (preset: string) => {
    const last = spec.data.length - 1;
    if (preset === 'ALL' || spec.xAxis.type !== 'time') {
      setVisibleRange([0, last]);
      setActiveRangePreset(preset);
      return;
    }
    const lastTime = chartTimeMilliseconds(spec.data[last][spec.xAxis.key]);
    if (lastTime === null) return;
    const dayCounts: Record<string, number> = {
      '1D': 1,
      '1W': 7,
      '5D': 5,
      '1M': 31,
      '3M': 93,
      '6M': 186,
      '1Y': 366,
      '2Y': 365 * 2,
      '3Y': 365 * 3,
      '5Y': 365 * 5,
    };
    const dayCount = dayCounts[preset] ?? 366;
    const threshold =
      preset === 'YTD'
        ? Date.UTC(new Date(lastTime).getUTCFullYear(), 0, 1)
        : lastTime - dayCount * 86_400_000;
    const start = Math.max(
      0,
      spec.data.findIndex((row) => {
        const value = chartTimeMilliseconds(row[spec.xAxis.key]);
        return value !== null && value >= threshold;
      }),
    );
    setVisibleRange([start, last]);
    setActiveRangePreset(preset);
  };

  const zoom = (direction: 'in' | 'out') => {
    const [start, end] = visibleRange;
    const span = end - start + 1;
    const delta = Math.max(1, Math.floor(span * 0.2));
    if (direction === 'in' && span > 4) {
      setVisibleRange([start + Math.floor(delta / 2), end - Math.ceil(delta / 2)]);
      setActiveRangePreset(null);
    }
    if (direction === 'out') {
      setVisibleRange([Math.max(0, start - delta), Math.min(spec.data.length - 1, end + delta)]);
      setActiveRangePreset(null);
    }
  };

  const navigatorValues = spec.data.map((row) => {
    const price = spec.series.find((item) => item.role === 'price') || spec.series[0];
    if (price?.mark === 'candlestick' && price.fieldMapping) {
      const value = Number(row[price.fieldMapping.close]);
      return Number.isFinite(value) ? value : null;
    }
    if (!price?.dataKey) return null;
    const value = Number(row[price.dataKey]);
    return Number.isFinite(value) ? value : null;
  });
  const showNavigator =
    spec.kind === 'timeseries' &&
    controls.allowZoom !== false &&
    spec.data.length >= NAVIGATOR_MIN_POINTS;
  const rangePresets = controls.rangePresets?.length
    ? controls.rangePresets
    : DEFAULT_RANGE_PRESETS;

  const pan = (direction: 'left' | 'right') => {
    const [start, end] = visibleRange;
    const shift = Math.max(1, Math.floor((end - start + 1) * 0.2));
    if (direction === 'left') {
      const nextStart = Math.max(0, start - shift);
      setVisibleRange([nextStart, nextStart + (end - start)]);
    } else {
      const nextEnd = Math.min(spec.data.length - 1, end + shift);
      setVisibleRange([nextEnd - (end - start), nextEnd]);
    }
    setActiveRangePreset(null);
  };
  const canPanLeft = visibleRange[0] > 0;
  const canPanRight = visibleRange[1] < spec.data.length - 1;
  const toggleExpanded = () => setExpanded((value) => !value);
  const dataThroughValue = spec.data.at(-1)?.[spec.xAxis.key];
  const dataThroughTime = chartTimeMilliseconds(dataThroughValue);
  const dataThroughDate =
    dataThroughTime !== null
      ? new Date(dataThroughTime).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: 'UTC',
        })
      : null;

  // A backtest that produced no rows used to draw an empty frame with axes and
  // controls, which reads as a broken chart rather than an empty result.
  const hasPlottableData = spec.data.length > 0 && spec.series.length > 0;

  // SAFETY: a custom property is a valid CSSProperties entry at runtime; the
  // React types simply do not model the custom-property index signature.
  const layerSurfaceStyle =
    layersAnchor === null
      ? undefined
      : ({
          ['--unified-chart-layers-anchor' as string]: `${layersAnchor}px`,
        } as React.CSSProperties);

  const viewControls = hasPlottableData ? (
    <div className="unified-chart__controls" aria-label="Chart view controls">
      {(controls.scaleModes || []).length > 0 && (
        <label className="unified-chart__scale-mode">
          <span>Price scale</span>
          <select
            value={scaleMode}
            onChange={(event) => setScaleMode(chartScaleMode(event.target.value))}
            aria-label="Price scale"
          >
            {(controls.scaleModes || []).map((mode) => (
              <option key={mode} value={mode}>
                {mode === 'log' ? 'Logarithmic' : mode[0].toUpperCase() + mode.slice(1)}
              </option>
            ))}
          </select>
        </label>
      )}
      {controls.allowZoom !== false && (
        <span
          className="unified-chart__control-group unified-chart__control-group--zoom"
          aria-label="Zoom controls"
        >
          <ZoomIcon data-testid="chart-zoom-icon" aria-hidden />
          <button
            className="unified-chart__zoom-step"
            type="button"
            onClick={() => zoom('out')}
            aria-label="Zoom out"
          >
            −
          </button>
          <button
            className="unified-chart__zoom-step"
            type="button"
            onClick={() => zoom('in')}
            aria-label="Zoom in"
          >
            +
          </button>
          <button
            className="unified-chart__zoom-reset"
            type="button"
            onClick={() => {
              setVisibleRange([0, Math.max(0, spec.data.length - 1)]);
              setActiveRangePreset('ALL');
            }}
            aria-label="Fit data"
          >
            Fit data
          </button>
        </span>
      )}
      {controls.allowPan !== false && (
        <span className="unified-chart__control-group" aria-label="Pan controls">
          <button
            type="button"
            onClick={() => pan('left')}
            aria-label="Pan left"
            disabled={!canPanLeft}
          >
            ←
          </button>
          <button
            type="button"
            onClick={() => pan('right')}
            aria-label="Pan right"
            disabled={!canPanRight}
          >
            →
          </button>
        </span>
      )}
    </div>
  ) : null;

  return (
    <section
      className={`unified-chart ${expanded ? 'unified-chart--expanded' : ''} unified-chart--labels-${effectiveLabelMode}`}
      data-testid="unified-chart"
      data-chart-kind={spec.kind}
      data-chart-profile={spec.profile || 'analytical'}
      data-chart-row-count={spec.data.length}
      data-chart-theme={theme}
      aria-label={spec.title}
    >
      <header className="unified-chart__header">
        <div className="unified-chart__header-row" data-testid="chart-header-actions">
          <div className="unified-chart__title-row" data-testid="chart-title-row">
            <h4>{spec.title}</h4>
            {symbolSelector ? (
              <div className="unified-chart__symbol-selector" data-testid="chart-symbol-selector">
                {symbolSelector}
              </div>
            ) : null}
          </div>
          {/* Zooming, fitting and expanding all answer one question: how much of
              the chart am I looking at. They share the title row so the symbol
              picker and Expand chart line up. Below 860px the view controls drop
              to their own full-width row rather than pushing Expand out of line. */}
          {viewControls}
          <button
            ref={expandButtonRef}
            type="button"
            className="unified-chart__expand"
            onClick={toggleExpanded}
            aria-label={expanded ? 'Exit full screen chart' : 'Expand chart'}
            aria-expanded={expanded}
          >
            {expanded ? <MinimizeIcon aria-hidden /> : <MaximizeIcon aria-hidden />}
            <span>{expanded ? 'Close expanded view' : 'Expand chart'}</span>
          </button>
        </div>
        {(spec.subtitle || sourceLabel || dataThroughDate || spec.explanation) && (
          <div className="unified-chart__header-copy">
            {spec.subtitle && <p className="unified-chart__subtitle">{spec.subtitle}</p>}
            {(sourceLabel || dataThroughDate) && (
              <p className="unified-chart__context" data-testid="chart-context">
                {[sourceLabel, dataThroughDate ? `Data through ${dataThroughDate}` : null]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
            {spec.explanation && (
              <details className="unified-chart__details" data-testid="chart-details">
                <summary>About this chart</summary>
                <p className="unified-chart__explanation">{spec.explanation}</p>
              </details>
            )}
          </div>
        )}
      </header>

      <div className="unified-chart__toolbar" data-testid="chart-toolbar">
        {displayModel.baseline && (
          <div className="unified-chart__viewbar">
            <span className="unified-chart__baseline" data-testid="chart-baseline">
              Baseline{' '}
              {displayModel.baseline.value.toLocaleString(undefined, { maximumFractionDigits: 4 })}
            </span>
          </div>
        )}
        {/* The rail and the panel it opens are one surface. The panel used to be
            a sibling pinned to the chart corner, so it opened far away from the
            button that opened it. */}
        {showVisibilityControls && hasPlottableData && (
          <div
            className="unified-chart__layer-surface"
            ref={layerSurfaceRef}
            style={layerSurfaceStyle}
          >
            <div
              className="unified-chart__legend-rail"
              aria-label="Visible chart objects"
              ref={railRef}
            >
              <div className="unified-chart__legend-chips">
                {spec.series.map((series, index) => {
                  const hidden =
                    hiddenSeries.has(series.id) ||
                    hiddenLayers.has(series.layerId || series.groupId || `series_${series.id}`);
                  return (
                    <button
                      key={series.id}
                      type="button"
                      className={hidden ? 'is-hidden' : ''}
                      aria-label={`${hidden ? 'Show' : 'Hide'} ${series.label}`}
                      aria-pressed={!hidden}
                      onClick={() =>
                        setHiddenSeries((current) => {
                          const next = new Set(current);
                          if (next.has(series.id)) next.delete(series.id);
                          else next.add(series.id);
                          return next;
                        })
                      }
                    >
                      {hidden ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
                      <i
                        className={`unified-chart__series-swatch unified-chart__series-swatch--${series.mark}`}
                        style={{ backgroundColor: resolveChartColor(series.style, index, theme) }}
                        aria-hidden
                      />
                      <span>{series.label}</span>
                    </button>
                  );
                })}
                {summaryLayers.map((layer, index) => {
                  const hidden = hiddenLayers.has(layer.id) || hiddenOverlayLayers.has(layer.id);
                  return (
                    <button
                      key={layer.id}
                      type="button"
                      className={hidden ? 'is-hidden' : ''}
                      aria-label={`${hidden ? 'Show' : 'Hide'} ${layer.label}`}
                      aria-pressed={!hidden}
                      onClick={() =>
                        setHiddenOverlayLayers((current) => {
                          const next = new Set(current);
                          if (next.has(layer.id)) next.delete(layer.id);
                          else next.add(layer.id);
                          return next;
                        })
                      }
                    >
                      {hidden ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
                      <i
                        className="unified-chart__series-swatch"
                        style={{
                          backgroundColor: resolveChartColor(layer.style, index, theme),
                          opacity: layer.style?.opacity,
                        }}
                        aria-hidden
                      />
                      <span>{layer.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* The chips scroll inside their own box and stop at this divider.
                  The control that opens the full list sits after them, the way
                  an overflow control on a toolbar does, rather than floating on
                  top of chips sliding underneath it. */}
              <button
                type="button"
                className="unified-chart__layer-more"
                ref={layersButtonRef}
                onClick={() => setLayersOpen((value) => !value)}
                aria-label="Chart layers"
                aria-expanded={layersOpen}
              >
                <LayersIcon aria-hidden />
                <span>Layers</span>
              </button>
            </div>
            {layersOpen && (
              <div
                className="unified-chart__layer-inspector"
                aria-label="Chart series and markers visibility"
              >
                <div className="unified-chart__inspector-heading">
                  <strong>Layers</strong>
                  <button
                    type="button"
                    onClick={() => setLayersOpen(false)}
                    aria-label="Close chart layers"
                  >
                    Close
                  </button>
                </div>
                <div className="unified-chart__layer-actions">
                  {controls.allowLayerSearch !== false && layers.length > 6 && (
                    <label>
                      <span className="sr-only">Search chart layers</span>
                      <input
                        type="search"
                        value={layerSearch}
                        onChange={(event) => setLayerSearch(event.target.value)}
                        placeholder="Search layers"
                        aria-label="Search chart layers"
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setHiddenSeries(new Set());
                      setHiddenLayers(new Set());
                      setHiddenOverlayLayers(new Set());
                    }}
                    aria-label="Show all chart layers"
                  >
                    Show all
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setHiddenSeries(new Set(spec.series.map((series) => series.id)));
                      setHiddenLayers(new Set(layers.map((layer) => layer.id)));
                      setHiddenOverlayLayers(new Set(layers.map((layer) => layer.id)));
                    }}
                    aria-label="Hide all chart layers"
                  >
                    Hide all
                  </button>
                  <button type="button" onClick={resetLayers} aria-label="Restore layer defaults">
                    Restore defaults
                  </button>
                  {controls.allowLabelControls !== false && (
                    <label className="unified-chart__label-mode">
                      <span>Labels</span>
                      <select
                        value={labelMode}
                        onChange={(event) => setLabelMode(chartLabelMode(event.target.value))}
                        aria-label="Chart label visibility"
                      >
                        <option value="auto">Auto</option>
                        <option value="hover">On hover</option>
                        <option value="always">Always</option>
                        <option value="hidden">Hidden</option>
                      </select>
                    </label>
                  )}
                </div>
                <div className="unified-chart__layer-list">
                  {filteredLayers.map((layer) => {
                    const hidden = hiddenLayers.has(layer.id);
                    const members = layerMembers(layer.id);
                    return (
                      <div className="unified-chart__layer-row" key={layer.id}>
                        <button
                          type="button"
                          className={hidden ? 'is-hidden' : ''}
                          aria-label={layer.label}
                          aria-pressed={!hidden}
                          onClick={() =>
                            setHiddenLayers((current) => {
                              const next = new Set(current);
                              if (next.has(layer.id)) next.delete(layer.id);
                              else next.add(layer.id);
                              return next;
                            })
                          }
                        >
                          {hidden ? <EyeOffIcon aria-hidden /> : <EyeIcon aria-hidden />}
                          <i
                            className="unified-chart__series-swatch"
                            style={{
                              backgroundColor: resolveChartColor(
                                layer.style,
                                layers.indexOf(layer),
                                theme,
                              ),
                              opacity: layer.style?.opacity,
                            }}
                            aria-hidden
                          />
                          <span>{layer.label}</span>
                        </button>
                        {members.length > 1 && (
                          <div className="unified-chart__layer-members">
                            {members.map((series) => (
                              <button
                                key={series.id}
                                type="button"
                                className={hiddenSeries.has(series.id) ? 'is-hidden' : ''}
                                aria-label={`${series.label} series`}
                                aria-pressed={!hiddenSeries.has(series.id)}
                                onClick={() =>
                                  setHiddenSeries((current) => {
                                    const next = new Set(current);
                                    if (next.has(series.id)) next.delete(series.id);
                                    else next.add(series.id);
                                    return next;
                                  })
                                }
                              >
                                {hiddenSeries.has(series.id) ? (
                                  <EyeOffIcon aria-hidden />
                                ) : (
                                  <EyeIcon aria-hidden />
                                )}
                                <span>{series.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {!hasPlottableData && (
        <div className="unified-chart__empty" data-testid="chart-empty-state" role="status">
          No chart data for this run yet.
        </div>
      )}

      {hasPlottableData && (
        <div className="unified-chart__plot" style={{ height: plotHeight }}>
          {renderedPanes.map((pane, index) => (
            <TimeSeriesPlot
              key={pane.id}
              syncId={chartSyncId}
              spec={renderedSpec || spec}
              pane={pane}
              height={(plotHeight * pane.weight) / totalWeight}
              showXAxis={index === renderedPanes.length - 1}
              visibleRange={visibleRange}
              activeIndex={activeIndex}
              hiddenSeries={hiddenSeries}
              hiddenLayers={hiddenLayers}
              hiddenOverlayLayers={hiddenOverlayLayers}
              scaleMode={scaleMode}
              onHover={setActiveIndex}
            />
          ))}
        </div>
      )}

      {showNavigator && hasPlottableData && (
        <ChartNavigator
          pointCount={spec.data.length}
          visibleRange={visibleRange}
          values={navigatorValues}
          presets={rangePresets}
          activePreset={activeRangePreset}
          onChange={(range) => {
            setVisibleRange(range);
            setActiveRangePreset(null);
          }}
          onSelectPreset={chooseRange}
        />
      )}

      {(sourceLabel || observedAt || spec.notes) && (
        <footer className="unified-chart__footer">
          {sourceLabel && <span>{sourceLabel}</span>}
          {observedAt && <span>{new Date(observedAt).toLocaleString()}</span>}
          {spec.notes && <span>{spec.notes}</span>}
        </footer>
      )}
    </section>
  );
}
